import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const TourContext = createContext({});

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
};

export const TOUR_STEPS = [
  // Step 0 — INTRODUCTION
  {
    id: 'intro',
    target: null,
    route: null,
    title: 'Bienvenido a MetalRecovery',
    description: 'Configura la herramienta con tus metales, costes operativos, % de pureza, descuentos sobre LME y días estimados hasta la venta. Calcula el precio máximo de compra de cada lote, gestiona el riesgo de inventario en tiempo real y analiza tus márgenes. ¡Realiza el tour y descubre el potencial de esta herramienta!',
    cta: 'Empezar el tour',
    ctaSkip: 'Saltar',
    position: 'center',
  },
  // Steps 1–7 — SETTINGS
  {
    id: 'settings-catalog-tab',
    target: '[data-tour="settings-catalog-tab"]',
    route: '/settings',
    title: 'Catálogo de Categorías',
    description: 'Lo primero es la configuración de la herramienta. Aquí defines cada tipo de chatarra que compras: el yield por defecto (% de metal recuperado), los costes de proceso, los días hasta venta y el descuento habitual. Cuanto más precisos sean estos datos, más exacto será tu precio de compra.',
    position: 'bottom',
  },
  {
    id: 'settings-metal-group',
    target: '[data-tour="settings-metal-group"]',
    route: '/settings',
    title: 'Grupos de Metal',
    description: 'Cada metal tiene su propio grupo con su tipo de benchmark: LME para metales cotizados (cobre, aluminio, zinc...) o Índice Regional para metales sin precio público directo (hierro, inox, latón).',
    position: 'bottom',
  },
  {
    id: 'settings-add-category',
    target: '[data-tour="settings-add-category"]',
    route: '/settings',
    title: 'Añade tus Categorías',
    description: 'Crea categorías por cada tipo de producto que compres o varias categorías de un mismo producto según sus calidades habituales.',
    position: 'bottom',
  },
  {
    id: 'settings-shock-tab',
    target: '[data-tour="settings-shock-tab"]',
    route: '/settings',
    title: 'Volatilidad por Metal',
    description: 'El buffer de riesgo dinámico usa estos valores históricos. A mayor volatilidad del metal, mayor protección en el precio de compra. Puedes dejar los valores calculados automáticamente o ajustarlos manualmente.',
    position: 'bottom',
  },
  {
    id: 'settings-risk-tab',
    target: '[data-tour="settings-risk-tab"]',
    route: '/settings',
    title: 'Umbrales de Riesgo',
    description: 'Define a partir de qué nivel de exposición total de tu inventario quieres recibir alertas. Adáptalos al tamaño de tu operación: una empresa pequeña y una grande necesitan umbrales muy distintos.',
    position: 'bottom',
  },
  {
    id: 'settings-feed-tab',
    target: '[data-tour="settings-feed-tab"]',
    route: '/settings',
    title: 'Feeds de Mercado',
    description: 'Configura los precios de referencia para metales sin benchmark público. Actualiza estos valores periódicamente con los precios reales de tu mercado local.',
    position: 'bottom',
  },
  {
    id: 'settings-audit-tab',
    target: '[data-tour="settings-audit-tab"]',
    route: '/settings',
    title: 'Auditoría de Cambios',
    description: 'Todo cambio en la configuración queda registrado aquí con usuario, fecha y valores anteriores/nuevos. Útil para entender cómo han evolucionado tus parámetros.',
    position: 'bottom',
  },
  // Steps 8–10 — MARKET DATA
  {
    id: 'market-benchmark-cards',
    target: '[data-tour="market-benchmark-cards"]',
    route: '/market-data',
    title: 'Benchmarks en Tiempo Real',
    description: 'Aquí verás los precios LME diarios en tiempo real. El sistema convierte automáticamente USD→EUR usando el tipo de cambio actual. Estos precios son la base de todos los cálculos de la app.',
    position: 'bottom',
  },
  {
    id: 'market-eurusd',
    target: '[data-tour="market-eurusd"]',
    route: '/market-data',
    title: 'Tipo de Cambio EUR/USD',
    description: 'El tipo de cambio es crítico — una variación del 2% en EUR/USD impacta directamente en el precio máximo de compra de todos tus lotes activos. Al estar conectado en tiempo real siempre se calculará el cambio €/$ en tiempo real.',
    position: 'bottom',
  },
  {
    id: 'market-manual-entry',
    target: '[data-tour="market-manual-entry"]',
    route: '/market-data',
    title: '¡Configuración Terminada!',
    description: 'Ya hemos configurado los datos. Ahora podemos empezar a utilizar la CALCULADORA de precios. ',
    position: 'bottom',
  },
  // Steps 11–13 — BATCH CALCULATOR
  {
    id: 'calc-metal-selector',
    target: '[data-tour="calc-metal-selector"]',
    route: '/batch-calculator',
    title: 'Selecciona el Metal',
    description: 'Selecciona el metal. El sistema carga el benchmark actual en EUR, las categorías con sus parámetros por defecto, y prepara el motor VaR con 90 días de histórico LME + EUR/USD para calcular el buffer de riesgo.',
    position: 'bottom',
  },
  {
    id: 'calc-inputs',
    target: '[data-tour="calc-inputs"]',
    route: '/batch-calculator',
    title: 'Peso, Yield y Costes',
    description: 'El peso neto y el yield son los inputs más importantes. El buffer de riesgo dinámico usa la volatilidad histórica combinada del metal en USD y del tipo de cambio EUR/USD — captura ambas fuentes de riesgo automáticamente.',
    position: 'bottom',
  },
  {
    id: 'calc-result',
    target: '[data-tour="calc-result"]',
    route: '/batch-calculator',
    title: 'Precio Máximo de Compra',
    description: 'Este es el precio máximo de seguridad. Si en la negociación decides pagar más, introduce el Precio de Compra Negociado para ver el riesgo asumido, el margen real resultante y el punto de equilibrio del lote.',
    position: 'left',
  },
  // Steps 14–15 — ACTIVE BATCHES
  {
    id: 'batch-table',
    target: '[data-tour="batch-table"]',
    route: '/active-batch-management',
    title: 'Gestión de Lotes',
    description: 'Aquí viven todos tus lotes desde que los compras hasta que los vendes. Cambia el estado conforme avanza el material y usa el historial para ver la evolución completa de cada lote.',
    position: 'bottom',
  },
  {
    id: 'batch-timeline-btn',
    target: '[data-tour="batch-timeline-btn"]',
    route: '/active-batch-management',
    title: 'Historial del Lote',
    description: 'El historial muestra la línea de tiempo completa: precio de compra, cambios de estado, ediciones y finalmente el margen realizado vs el estimado al comprar. Es tu herramienta de aprendizaje y calibración.',
    position: 'left',
  },
  // Steps 16–17 — INVENTORY
  {
    id: 'inventory-exposure',
    target: '[data-tour="inventory-exposure"]',
    route: '/inventory',
    title: 'Exposición de Inventario',
    description: 'Tu exposición total es el valor de mercado actual de todo el metal que tienes en stock. Si el mercado cae un 5%, esta es la pérdida potencial que verás reflejada aquí.',
    position: 'bottom',
  },
  {
    id: 'inventory-stress',
    target: '[data-tour="inventory-stress"]',
    route: '/inventory',
    title: 'Stress Test de Inventario',
    description: 'Los escenarios de caída (-3%, -5%, -10%) muestran cuánto perderías si el benchmark baja esos porcentajes hoy. Úsalos para decidir si cubres posiciones o aceleras ventas.',
    position: 'top',
  },
  // Steps 18–19 — ALERTS
  {
    id: 'alerts-active',
    target: '[data-tour="alerts-active"]',
    route: '/alerts-dashboard',
    title: 'Alertas Activas',
    description: 'Las alertas se generan automáticamente cuando se superan los umbrales que configuraste en Ajustes. Reconoce las que ya has visto y resuelve las que hayas atendido.',
    position: 'bottom',
  },
  {
    id: 'alerts-rules',
    target: '[data-tour="alerts-rules"]',
    route: '/alerts-dashboard',
    title: 'Reglas de Alerta',
    description: 'Define qué condiciones disparan una alerta: exposición crítica, lotes antiguos, márgenes bajos, movimientos bruscos de benchmark. Cada recuperador necesita sus propios umbrales.',
    position: 'top',
  },
  // Steps 20–21 — SALES ANALYSIS
  {
    id: 'sales-kpis',
    target: '[data-tour="sales-kpis"]',
    route: '/sales-analysis',
    title: 'Resultados Reales',
    description: 'Aquí ves el margen que realmente has ganado, comparado con lo que estimaste al comprar. La diferencia entre margen estimado y realizado es tu indicador de precisión de la gestión que realizas.',
    position: 'bottom',
  },
  {
    id: 'sales-yield-calibration',
    target: '[data-tour="sales-yield-calibration"]',
    route: '/sales-analysis',
    title: 'Calibración de Yield',
    description: 'Si el yield real sistemáticamente difiere del estimado en una categoría, es señal de que debes ajustar el default en Configuración. Mejor calibración = precios de compra más precisos = más margen.',
    position: 'top',
  },
  // Steps 22–24 — DASHBOARD
  {
    id: 'kpi-cards',
    target: '[data-tour="kpi-cards"]',
    route: '/main-dashboard',
    title: 'Vista Global del Negocio',
    description: 'El Dashboard muestra tu riesgo operativo en tiempo real: exposición total, pérdidas potenciales, lotes en riesgo y el panel de inventario con P&L latente por lote. Los benchmarks detallados están en la pantalla Mercado.',
    position: 'bottom',
  },
  {
    id: 'dashboard-scenarios',
    target: '[data-tour="dashboard-scenarios"]',
    route: '/main-dashboard',
    title: 'Simulador de Escenarios',
    description: 'El simulador permite elegir el alcance (todo el inventario, por metal o categoría), el tipo de shock (benchmark, EUR/USD o combinado) y la magnitud. Muestra qué lotes entrarían en pérdida y genera una recomendación automática.',
    position: 'top',
  },
  // Step 24 — FINAL
  {
    id: 'final',
    target: null,
    route: '/main-dashboard',
    title: 'Tour completado ✓',
    description: 'Ya conoces toda la plataforma. Recuerda: la precisión de MetalRecovery depende de la calidad de tus datos. Actualiza precios diariamente, calibra yields con ventas reales y ajusta umbrales a tu operación. El panel lateral \'?\' está siempre disponible si necesitas ayuda en cualquier pantalla.',
    cta: 'Ir al Dashboard',
    position: 'center',
  },
];

export function TourProvider({ children }) {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [navigatingTo, setNavigatingTo] = useState(null);
  const userIdRef = useRef(null);

  const setUserId = useCallback((id) => {
    userIdRef.current = id;
  }, []);

  const startTour = useCallback(() => {
    setStepIndex(0);
    setActive(true);
    try { localStorage.setItem('tour_active', 'true'); } catch {}
  }, []);

  const resetTour = useCallback(() => {
    setStepIndex(0);
    setActive(true);
    try {
      localStorage.removeItem('tour_completed');
      localStorage.setItem('tour_active', 'true');
    } catch {}
  }, []);

  const completeTour = useCallback(async () => {
    setActive(false);
    setNavigatingTo(null);
    try { localStorage.setItem('tour_completed', 'true'); localStorage.removeItem('tour_active'); } catch {}
    const userId = userIdRef?.current;
    if (userId) {
      try {
        const { data: profile } = await supabase?.from('user_profiles')?.select('preferences')?.eq('id', userId)?.single();
        const prefs = profile?.preferences || {};
        await supabase?.from('user_profiles')?.update({ preferences: { ...prefs, tour_completed: true } })?.eq('id', userId);
      } catch {}
    }
  }, []);

  const skipTour = useCallback(() => {
    completeTour();
  }, [completeTour]);

  const nextStep = useCallback(() => {
    const nextIndex = stepIndex + 1;
    if (nextIndex >= TOUR_STEPS?.length) {
      completeTour();
      return;
    }
    const nextStepData = TOUR_STEPS?.[nextIndex];
    const currentRoute = window.location?.pathname;
    const targetRoute = nextStepData?.route;

    if (targetRoute && targetRoute !== currentRoute) {
      setNavigatingTo(targetRoute);
      navigate(targetRoute);
      setTimeout(() => {
        setStepIndex(nextIndex);
        setNavigatingTo(null);
      }, 400);
    } else {
      setStepIndex(nextIndex);
    }
  }, [stepIndex, navigate, completeTour]);

  const prevStep = useCallback(() => {
    const prevIndex = Math.max(0, stepIndex - 1);
    const prevStepData = TOUR_STEPS?.[prevIndex];
    const currentRoute = window.location?.pathname;
    const targetRoute = prevStepData?.route;

    if (targetRoute && targetRoute !== currentRoute) {
      setNavigatingTo(targetRoute);
      navigate(targetRoute);
      setTimeout(() => {
        setStepIndex(prevIndex);
        setNavigatingTo(null);
      }, 400);
    } else {
      setStepIndex(prevIndex);
    }
  }, [stepIndex, navigate]);

  const isTourCompleted = useCallback(() => {
    try { return localStorage.getItem('tour_completed') === 'true'; } catch { return false; }
  }, []);

  const value = {
    active,
    stepIndex,
    currentStep: TOUR_STEPS?.[stepIndex] || null,
    totalSteps: TOUR_STEPS?.length,
    startTour,
    resetTour,
    nextStep,
    prevStep,
    completeTour,
    skipTour,
    isLastStep: stepIndex === TOUR_STEPS?.length - 1,
    isFirstStep: stepIndex === 0,
    navigatingTo,
    isTourCompleted,
    setUserId,
  };

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export default TourContext;
